import React, { useState, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Upload, X, Loader2 } from 'lucide-react';
import { uploadImage, resolveImageUrl, formatApiError } from '../../services/api';

const ImageUploader = ({ value, onChange, label = 'Image' }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const result = await uploadImage(file);
      onChange(result.url);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-amber-300 uppercase tracking-wider text-xs font-semibold" style={{ fontFamily: 'Cinzel, serif' }}>
        {label}
      </label>

      <div className="flex flex-col gap-3">
        {/* Image preview */}
        {value && (
          <div className="relative w-full max-w-xs">
            <img
              src={resolveImageUrl(value)}
              alt="preview"
              className="w-full h-40 object-cover rounded-lg border border-amber-600/30"
            />
            <button
              type="button"
              onClick={() => onChange('')}
              className="absolute top-2 right-2 w-7 h-7 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700"
              data-testid="image-remove-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="flex gap-3 items-center flex-wrap">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
          />
          <Button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="btn-mystical"
            data-testid="image-upload-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? 'Uploading...' : 'Upload Image'}
          </Button>

          <span className="text-sm text-amber-100/60">or paste image URL:</span>

          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://..."
            className="bg-emerald-950/60 border-amber-600/30 text-amber-100 flex-1 min-w-[200px]"
            data-testid="image-url-input"
          />
        </div>

        {error && <p className="text-red-300 text-sm">{error}</p>}
        <p className="text-xs text-amber-100/50">JPG, PNG, WEBP, GIF — max 8 MB</p>
      </div>
    </div>
  );
};

export default ImageUploader;
